import * as React from 'react';
import { cn } from '@core/lib/utils';
import { Input } from '@core/components/ui/shared/inputs/input';
import { Label } from '@core/components/ui/shared/labels/label';
import { Button } from '../buttons/button';
import { XIcon } from 'lucide-react';
import { LinkPreview } from '@core/components/ui/shared/previewers/link-preview';
import { ExternalLink } from 'lucide-react';

export type InputProps = React.InputHTMLAttributes<HTMLInputElement> & {
  label?: string;
  onClear?: () => void;
  url?: string;
}

const FloatingInput = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, label, placeholder, id, onClear, url, readOnly, value, ...props }, ref) => {
    const generatedId = React.useId();
    const inputId = id || generatedId;
    const [hasValue, setHasValue] = React.useState(false);

    return (
      <div className={cn('relative group mt-2', className)}>
        <Input
          placeholder=''
          className={cn('peer border-border h-10', className)}
          ref={ref}
          id={inputId}
          onChange={(e) => {
            setHasValue(e.target.value !== '');
            if (props.onChange) {
              props.onChange(e);
            }
          }}
          value={value ?? ''}
          {...props}
        />
        <div className='absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-1'>
          {url && (
            <LinkPreview url={url} className='block' quality={100} width={200} height={200} layout='left'>
              <Button
                type='button'
                variant='ghost'
                size='icon'
                className='h-7 w-7 text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 transition-opacity opacity-0 group-hover:opacity-100'
                aria-label='Preview URL'
              >
                <ExternalLink className='h-4 w-4' />
              </Button>
            </LinkPreview>
          )}
          {!readOnly && (
            <Button
              type='button'
              variant='ghost'
              size='icon'
              className='h-7 w-7 text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 transition-opacity opacity-0 group-hover:opacity-100'
              onClick={() => {
                const inputElement = document.getElementById(inputId) as HTMLInputElement;
                if (inputElement) {
                  inputElement.value = '';
                  setHasValue(false);
                  inputElement.dispatchEvent(new Event('input', { bubbles: true }));
                }
                if (onClear) {
                  onClear();
                }
              }}
            >
              <XIcon className='h-4 w-4' />
            </Button>
          )}
        </div>
        <Label
          htmlFor={inputId}
          className={cn(
            'absolute left-3 top-0 z-10 origin-left -translate-y-1/2 scale-75 transform bg-background px-1 text-sm text-muted-foreground transition-all duration-200',
            'peer-placeholder-shown:top-1/2 peer-placeholder-shown:scale-100 peer-placeholder-shown:bg-transparent peer-placeholder-shown:text-muted-foreground',
            'peer-focus:top-0 peer-focus:-translate-y-1/2 peer-focus:scale-75 peer-focus:bg-background peer-focus:px-1 peer-focus:text-primary',
            'peer-[:not(:placeholder-shown)]:top-0 peer-[:not(:placeholder-shown)]:-translate-y-1/2 peer-[:not(:placeholder-shown)]:scale-75 peer-[:not(:placeholder-shown)]:bg-background peer-[:not(:placeholder-shown)]:px-1',
            'cursor-text pointer-events-none select-none',
          )}
        >
          {label || placeholder}
        </Label>
      </div>
    );
  },
);
FloatingInput.displayName = 'FloatingInput';

const FloatingLabel = React.forwardRef<
  React.ComponentRef<typeof Label>,
  Omit<React.ComponentProps<typeof Label>, 'ref'>
>(({ className, ...props }, ref) => {
  return (
    <Label
      className={cn(
        'absolute start-2 top-2 z-10 origin-[0] -translate-y-4 scale-75 transform bg-background px-2 text-sm text-muted-foreground duration-300',
        'peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:scale-100 peer-placeholder-shown:bg-transparent',
        'peer-focus:top-2 peer-focus:-translate-y-4 peer-focus:scale-75 peer-focus:bg-background peer-focus:px-2 peer-focus:text-foreground',
        'peer-[:not(:placeholder-shown)]:top-2 peer-[:not(:placeholder-shown)]:-translate-y-4 peer-[:not(:placeholder-shown)]:scale-75 peer-[:not(:placeholder-shown)]:bg-background peer-[:not(:placeholder-shown)]:px-2',
        'rtl:peer-focus:left-auto rtl:peer-focus:translate-x-1/4 cursor-text pointer-events-none',
        className,
      )}
      ref={ref}
      {...props}
    />
  );
});
FloatingLabel.displayName = 'FloatingLabel';

type FloatingLabelInputProps = InputProps & { label?: string };

const FloatingLabelInput = React.forwardRef<
  React.ComponentRef<typeof Input>,
  React.PropsWithoutRef<FloatingLabelInputProps>
>(({ id, label, placeholder, className, ...props }, ref) => {
  const generatedId = React.useId();
  const inputId = id || generatedId;

  return (
    <div className="relative">
      <Input
        ref={ref}
        id={inputId}
        placeholder=" "
        className={cn('peer', className)}
        {...props}
      />
      <FloatingLabel htmlFor={inputId}>
        {label || placeholder}
      </FloatingLabel>
    </div>
  );
});
FloatingLabelInput.displayName = 'FloatingLabelInput';

export { FloatingInput, FloatingLabel, FloatingLabelInput };
